import { combineSets } from '@/utils/combineSets'

const apiTypeNames = {
  actor: 'TYPE_ACTOR',
  malware: 'TYPE_MALWARE',
  campaign: 'TYPE_CAMPAIGN',
  report: 'TYPE_REPORT',
  vulnerability: 'TYPE_VULN',
  indicator: 'TYPE_INDICATOR',
  tool: 'TYPE_TOOL',
  location: 'TYPE_GEO',
  industry: 'TYPE_INDUSTRY',
  mitre_tactic: 'TYPE_MITRE_TACTIC',
  mitre_technique: 'TYPE_MITRE_TECHNIQUE',
  alias: 'TYPE_ALIAS',
  motivation: 'TYPE_MOTIVATION',
}

const interestDefinitions = [
  {
    type: 'actor',
    idPrefix: 'threat-actor--',
    profileField: 'actors',
    apiType: 'INTEREST_TYPE_ACTOR',
  },
  {
    type: 'campaign',
    idPrefix: 'campaign--',
    profileField: 'campaigns',
    apiType: 'INTEREST_TYPE_CAMPAIGN',
  },
  {
    type: 'malware',
    idPrefix: 'malware--',
    profileField: 'malwares',
    apiType: 'INTEREST_TYPE_MALWARE',
  },
  {
    type: 'vulnerability',
    idPrefix: 'vulnerability--',
    profileField: 'vulnerabilities',
    apiType: 'INTEREST_TYPE_VULNERABILITY',
  },
  {
    type: 'location',
    idPrefix: 'location--',
    profileField: 'targetGeos',
    apiType: 'INTEREST_TYPE_TARGET_GEO',
  },
  {
    type: 'industry',
    idPrefix: 'identity--',
    profileField: 'industries',
    apiType: 'INTEREST_TYPE_INDUSTRY',
  },
]

const interestDefinitionsByApiType = interestDefinitions.reduce((memo, def) => {
  memo[def.apiType] = def
  return memo
}, {})

/**
 * Computed following data from a profile to assist in lookups, contains a per-type dict of Sets
 * including followed IDs for each type, and a superset of all followed IDs cross type.
 *
 * @typedef {Object} FollowingData
 * @property {Object.<string, Set>} map - a dict of interest types and following sets for each
 * @property {Set} set - a set of all followed IDs of any type
 */

/**
 * A Threat Profiles API Interest object
 *
 * @typedef {Object} Interest
 * @property {string} value
 * @property {string} type
 * @property {boolean} isDirectFollow
 * @property {boolean} isAiRecommendation
 */

/**
 * Takes a profile and an update which is sent to the interest mutation hook and applies the
 * update optimistically to the given profile. This is used by the interest mutation hook to
 * perform immediate updates on the profile cache for instant feedback when updating follows.
 *
 * @param {Object} profile
 * @param {Object} update - the update/vars passed to the interest mutation hook. expects `type` of
 *   "add" or "remove" and an `interest` which can be an object ID, threat profile interest, or
 *   an array combining either
 * @return {{ profile: Object, following: FollowingData }}
 * @private
 */
export const buildOptimisticInterestMutation = (profile, update) => {
  profile = JSON.parse(JSON.stringify(profile))

  const interests = normalizeInterests(update.interest)
  const shouldFollow = update.type === 'add'

  for (const interest of interests) {
    const { profileField } = interestDefinitionsByApiType[interest.type]
    const field = profile[profileField] || []
    const existingInterest = field.find(item => item.value === interest.value)
    const { isAiRecommendation = false, isDirectFollow = false } = existingInterest || {}

    // If the follow state already matches the existing state, continue.
    if (isDirectFollow === shouldFollow) {
      continue
    }

    if (isAiRecommendation) {
      // If this is an AI recommendation just toggle following.
      existingInterest.isDirectFollow = shouldFollow
    } else if (shouldFollow) {
      // Otherwise if we're to follow, we can assume there is no existing interest and we can just
      // push a new followed interest to the array.
      field.push({ ...interest, isDirectFollow: true })
    } else {
      // And finally if it's not an AI recommendation and we're unfollowing, we can just remove it
      // from the array.
      field.splice(field.indexOf(existingInterest), 1)
    }
  }

  return {
    profile,
    following: buildProfileFollowingData(profile),
  }
}

/**
 * Takes a profile and an update which is sent to the recommendation feedback mutation hook and
 * applies the update optimistically to the given profile. This is used by the recommendation
 * feedback mutation hook to perform immediate updates on the profile cache for instant feedback
 * when updating recommendationFeedback.
 *
 * @param {Object} profile
 * @param {Object} update - the update/vars passed to the recommendation feedback mutation hook.
 *   expects `interest` which can be an object ID, threat profile interest, or an array combining
 *   either
 * @return {{ profile: Object, following: FollowingData }}
 * @private
 */
export const buildOptimisticRecommendationFeedbackMutation = (profile, update) => {
  profile = structuredClone(profile)

  const interests = normalizeInterests(update.interest)

  for (const interest of interests) {
    const { profileField } = interestDefinitionsByApiType[interest.type]
    const field = profile[profileField] || []
    const existingInterest = field.find(item => item.value === interest.value)

    if (!existingInterest) {
      continue
    }

    existingInterest.recommendationFeedback = update?.feedback ? normalizeFeedback[update.feedback] : normalizeFeedback.neutral
  }

  return {
    profile,
    following: buildProfileFollowingData(profile),
  }
}

/**
 * Builds the following sets for all types of interests in a profile. This data is used fof easy
 * lookup of follows by set inclusion vs repeatedly delving into the profile object.
 *
 * @param {Object} profile - a profile returned from the Threat Profiles API
 * @return {FollowingData}
 * @private
 */
export const buildProfileFollowingData = profile => {
  if (!profile) {
    return {}
  }

  const map = interestDefinitions.reduce((map, { type, profileField }) => {
    const items = profile[profileField] || []

    const followedItemIds = items
      .filter(item => item.isDirectFollow)
      .map(item => item.value)

    return { ...map, [type]: new Set(followedItemIds) }
  }, {})

  map.all = combineSets(...Object.values(map))

  return map
}

/**
 * Takes an id and looks up the corresponding interest definition, will return undefined if
 * unsuccessful
 *
 * @param {string} id
 * @return {Interest|undefined}
 * @private
 */
export const getInterestDefinitionById = id => {
  if (typeof id !== 'string') {
    return
  }
  return interestDefinitions.find(({ idPrefix }) => {
    return id.startsWith(idPrefix)
  })
}

/**
 * Takes a single id/object or an array of id/object and normalizes them into a validated list of
 * interest objects.
 *
 * @param {string|Interest|Array.<string, Interest>} arg - a single ID/interest or an array
 *   including a mix of either
 * @return {Interest[]} - a list of interest objects
 * @private
 */
export const normalizeInterests = arg => {
  const isInterest = obj => !!(obj?.type && interestDefinitionsByApiType[obj.type])

  return [].concat(arg).reduce((memo, value) => {
    let def
    // If id is an object assume it's an actual interest object and not an ID. We want the client
    // to be flexible to both scenarios so we can update existing interests, or create/remove based
    // on ID alone with a derived type
    if (isInterest(value)) {
      memo.push(value)
    } else if ((def = getInterestDefinitionById(value))) {
      memo.push({ value, type: def.apiType })
    }
    return memo
  }, [])
}

export const normalizeFeedback = {
  like: 'RECOMMENDATION_FEEDBACK_GOOD',
  dislike: 'RECOMMENDATION_FEEDBACK_BAD',
  neutral: 'RECOMMENDATION_FEEDBACK_UNSPECIFIED'
}

export const ensureValidAipId = (subjectId, namespaces = []) => {
  const uuidRe = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[89ab][\da-f]{3}-[\da-f]{12}$/i

  if (typeof subjectId !== 'string') {
    throw new Error(`Unexpected ID/name, expected string, got ${typeof subjectId}`)
  }

  if (uuidRe.test(subjectId)) {
    return subjectId
  }

  const parts = subjectId.split('/')

  const raiseInvalid = () => {
    throw new Error(
      `Unexpected ID/name: ${subjectId}, ` +
      `expected UUID or name with with namespaces [${namespaces.join(', ')}]`
    )
  }

  if (parts.length === 1 || parts.length !== namespaces.length * 2) {
    raiseInvalid()
  }

  return parts.reduce((memo, part, i) => {
    const validPart = i % 2 === 0 ? parts[i] === namespaces[i / 2] : uuidRe.test(parts[i])
    if (!validPart) { raiseInvalid() }
    return parts[i]
  }, '')
}

export const ensureValidProfileId = subjectId => {
  return ensureValidAipId(subjectId, ['profiles'])
}

export const ensureValidProfileEventId = subjectId => {
  return ensureValidAipId(subjectId, ['profiles', 'events'])
}

export const groupInterestsListByProfileField = interests => {
  if (!Array.isArray(interests)) {
    return {}
  }
  return interests.reduce((memo, interest) => {
    const def = getInterestDefinitionById(interest)
    if (!def?.profileField) {
      return memo
    }
    memo[def.profileField] = memo[def.profileField] || []
    memo[def.profileField].push(normalizeInterests(interest)[0])
    return memo
  }, {})
}

export const buildEventsFilter = (options = {}) => {
  const types = [].concat(options.type || [])
  const parseDateOption = key => {
    if (!options[key]) return
    const date = new Date(options[key])
    return isNaN(date) ? undefined : `${date.toISOString().slice(0, -5)}Z`
  }
  const publishedBefore = parseDateOption('publishedBefore')
  const publishedAfter = parseDateOption('publishedAfter')

  const filters = []
  const publishTimeFilter = { join: 'AND', items: [] }

  if (publishedAfter) {
    publishTimeFilter.items.push(`publish_time > "${publishedAfter}"`)
  }

  if (publishedBefore) {
    publishTimeFilter.items.push(`publish_time < "${publishedBefore}"`)
  }

  if (publishTimeFilter.items.length) {
    filters.push(publishTimeFilter)
  }

  const [typeInclusionFilter, typeExclusionFilter] = types.reduce((sets, type) => {
    const apiType = apiTypeNames[type.replace(/^!/, '')]
    if (!apiType) {
      return sets
    }
    const set = sets[type.startsWith('!') ? 1 : 0]
    set.items.push(`mati_entity_type = "${apiType}"`)
    return sets
  }, [
    { items: [], join: 'OR' },
    { items: [], join: 'AND', negate: true }
  ])

  if (typeInclusionFilter.items.length) {
    filters.push(typeInclusionFilter)
  } else if (typeExclusionFilter.items.length) {
    filters.push(typeExclusionFilter)
  }

  if (!filters.length) {
    return
  }

  const proc = filter => {
    return filter.items
      .map(item => filter.negate ? `NOT (${item})` : item)
      .join(` ${filter.join} `)
  }

  if (filters.length === 1) {
    return proc(filters[0])
  }

  return filters.map(filter => {
    const value = proc(filter)
    return filter.items.length > 1 ? `(${value})` : value
  }).join(' AND ')
}
